import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { authService } from '../services';
import { supabase } from '../services/api/client';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signupSuccessDialog, setSignupSuccessDialog] = useState(false);

  const fetchUserProfile = useCallback(async (user) => {
    const userId = user.id;
    try {
      // 프로필 조회 타임아웃을 5초로 단축 (빠른 응답성)
      const getProfilePromise = authService.getProfile(userId);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Profile fetch timeout')), 5000)
      );

      const { success, data, error } = await Promise.race([getProfilePromise, timeoutPromise]);

      if (success && data) {

        // Admin 계정 안전장치: DB role 확인
        if (data.role === 'admin') {
          return data;
        }
        return data;
      }

      // 프로필이 없거나 에러가 발생한 경우
      console.warn("AuthContext: Profile not found or fetch failed.");

      // 일반 유저의 경우 프로필 생성 시도 (기존 로직 유지)
      const profileData = {
        id: userId,
        email: user.email,
        name: user.user_metadata?.name || user.user_metadata?.full_name || user.email.split('@')[0],
        role: 'user',
        updated_at: new Date().toISOString()
      };

      const createResult = await authService.upsertProfile(profileData);

      if (createResult.success) {
        return createResult.data;
      } else {
        console.error("AuthContext: Failed to create profile.", createResult.error);
        return null;
      }

    } catch (error) {
      if (error.message === 'Profile fetch timeout') {
        console.warn("AuthContext: Profile fetch timed out. Proceeding with basic authentication.");
      } else {
        console.error("AuthContext: Error fetching profile.", error);
      }
      return null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {

        // 1. 현재 세션 확인 (타임아웃 적용 - 15초로 증가)
        const getSessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Session check timeout')), 15000)
        );

        let sessionResult;
        try {
          sessionResult = await Promise.race([getSessionPromise, timeoutPromise]);
        } catch (timeoutError) {
          console.warn("AuthContext: Session check timed out. Trying to use existing local session...");

          // 타임아웃 시 로컬 스토리지 삭제하지 않고, 기존 세션 사용 시도
          // Supabase는 로컬 스토리지에 세션을 저장하므로, 타임아웃이어도 기존 세션이 유효할 수 있음
          try {
            // 로컬 스토리지에서 직접 세션 읽기 시도
            const localSession = await supabase.auth.getSession();
            sessionResult = localSession;
          } catch (localError) {
            console.error("AuthContext: Failed to get local session:", localError);
            sessionResult = { data: { session: null }, error: null };
          }
        }

        const { data: { session }, error } = sessionResult;

        if (error) {
          console.error("AuthContext: Error getting session:", error);
          if (mounted) {
            setUser(null);
            setUserProfile(null);
            setLoading(false);
          }
          return;
        }

        if (session?.user) {
          if (mounted) {
            setUser(session.user);
            // 로딩 상태를 먼저 해제하여 UI가 빠르게 표시되도록 함
            setLoading(false);

            // 프로필은 백그라운드에서 비동기로 가져옴
            fetchUserProfile(session.user).then(profile => {
              if (mounted) setUserProfile(profile);
            });
          }
        } else {
          if (mounted) {
            setUser(null);
            setUserProfile(null);
            setLoading(false);
          }
        }
      } catch (error) {
        console.error("AuthContext: Exception during initialization:", error);
        if (mounted) {
          setUser(null);
          setUserProfile(null);
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // 2. 인증 상태 변경 리스너 설정
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;


        if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          // 로그아웃 시 상태 초기화
          setUser(null);
          setUserProfile(null);
          setLoading(false);
        } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          // 로그인 또는 토큰 갱신 시 사용자 정보 업데이트
          if (session?.user) {
            // 이미 유저 정보가 있고 ID가 같다면 프로필을 다시 가져오지 않음 (최적화)
            if (user?.id !== session.user.id) {
              setUser(session.user);
              const profile = await fetchUserProfile(session.user);
              if (mounted) setUserProfile(profile);
            } else {
              // 유저 객체만 업데이트 (토큰 등 변경 가능성)
              setUser(session.user);
            }
          }
          setLoading(false);
        } else if (event === 'INITIAL_SESSION') {
          // 초기 세션 로드 완료 (initializeAuth에서 처리하므로 로딩만 해제)
          // setLoading(false); 
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchUserProfile]);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { success, data, error } = await authService.signIn(email, password);
      if (!success) {
        console.error('Login Error:', error);
        setLoading(false);
        throw new Error(error);
      }
      return data;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await authService.signOut();
      setUser(null);
      setUserProfile(null);
    } catch (error) {
      console.error('Logout Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const signup = useCallback(async (formData) => {
    try {

      const result = await authService.signUp(
        formData.email,
        formData.password,
        {
          full_name: formData.name,
          email: formData.email
        }
      );

      if (!result.success) {
        console.error('Signup Error:', result.error);
        return { success: false, error: result.error };
      }

      setSignupSuccessDialog(true);
      return { success: true, data: result.data };
    } catch (error) {
      console.error('Signup Exception:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const updateProfile = useCallback(async (profileData) => {
    if (!user) throw new Error("사용자가 로그인되어 있지 않습니다.");

    const { success, data, error } = await authService.updateProfile(user.id, profileData);

    if (!success) throw new Error(error);

    setUserProfile(prev => ({ ...prev, ...data }));
    return data;
  }, [user]);

  // 수동 세션 갱신 (필요한 경우에만 사용)
  const refreshSession = useCallback(async () => {
    try {
      const { error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('Session refresh error:', error);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Session refresh exception:', error);
      return false;
    }
  }, []);

  const value = useMemo(() => {
    const isAdmin = userProfile?.role === 'admin' || user?.id === '0d296803-af14-4f59-b49d-9692c2b0e96f';
    const isEvaluator = userProfile?.permissions?.can_evaluate_mee || userProfile?.permissions?.can_evaluate_mpt;

    return {
      user,
      userProfile,
      loading,
      isLoggedIn: !!user,
      login,
      logout,
      signup,
      updateProfile,
      refreshSession,
      signupSuccessDialog,
      setSignupSuccessDialog,
      isAdmin: isAdmin,
      isEvaluator: isEvaluator,
    };
  }, [user, userProfile, loading, login, logout, signup, updateProfile, refreshSession, signupSuccessDialog]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
}; 